import type {
  BatchDocumentResponse,
  BatchResponseEntryKind,
  BatchCheckResponseState,
  BatchLineResponseSourceType,
  BatchResponse,
  BatchResponseReadiness,
  BatchResponseTab,
} from '@api/model';

import type {PillStyle} from '@/components/DotStatusPill';

/**
 * The journal-entry queue's own vocabulary: how a batch's readiness reads, what
 * each claim and voucher kind is called, and the queue's totals. Periods live
 * in `period.ts`, credits in `credits.ts`, the SQL Account read-back in
 * `upstream.ts`.
 *
 * `readiness` is NOT the batch's `status`, and the distinction is the whole
 * point of the page. `status` is `draft` / `posted` -- whether it has gone.
 * `readiness` is whether it CAN go, which the server derives from the same
 * preflight the detail panel lists, so the queue's pill and the checks beside
 * the grid can never disagree about whether an entry is postable.
 */

/** The tone each readiness is painted in. `dotGlow` marks the states still in play. */
export const READINESS_STYLE: Record<BatchResponseReadiness, PillStyle> = {
  ready: {
    color: 'var(--dyna-accent-text)',
    border: 'rgba(var(--dyna-accent-rgb),0.42)',
    bg: 'var(--dyna-accent-soft)',
    dotGlow: true,
  },
  // Warm amber: postable, but something on it is worth a second look --
  // visually distinct from the cooler `ready`, matching the advance pills'
  // `partially_approved`.
  check: {
    color: 'var(--dyna-warn)',
    border: 'rgba(240,182,109,0.35)',
    bg: 'rgba(240,182,109,0.08)',
    dotGlow: true,
  },
  blocked: {
    color: 'var(--dyna-err)',
    border: 'rgba(255,107,107,0.4)',
    bg: 'rgba(255,107,107,0.08)',
    dotGlow: true,
  },
  // The end of the line: green like a settled advance, but FLAT -- nothing is
  // in flight any more, so it loses the glow the active states carry.
  posted: {
    color: 'var(--dyna-ok)',
    border: 'rgba(52,211,153,0.35)',
    bg: 'rgba(52,211,153,0.08)',
    dotGlow: false,
  },
};

/**
 * Whether this entry may be posted at all.
 *
 * One definition, because two surfaces answer it about the same row: the rail
 * disables the tick on anything unpostable, and the page counts what is ticked
 * against the same set. When each wrote its own copy they could disagree, and
 * the failure was silent -- a row drawn ticked while the bulk bar refused to
 * count it.
 */
export const isPostable = (batch: {readiness: BatchResponseReadiness}): boolean =>
  batch.readiness === 'ready' || batch.readiness === 'check';

/** What the action bar says about an entry that has not gone yet. */
export const READINESS_MESSAGE: Record<BatchResponseReadiness, string> = {
  ready: 'Everything resolves. Ready to post.',
  check: 'You can post as it stands. The flagged debits will land with no project.',
  blocked: 'Clear the failed checks before this entry can post.',
  // Unreachable from the action bar, which answers for a posted entry with
  // its journal number before it gets here. Present so the table is total.
  posted: 'Posted to SQL Account.',
};

/**
 * How one preflight check reads: its glyph and the weight of its row.
 *
 * A `Record` over the generated union rather than three parallel ternaries --
 * the glyph, its colour and the message's tone were each decided separately,
 * so a fourth state would have had to find all three.
 */
export const CHECK_STYLE: Record<
  BatchCheckResponseState,
  {glyph: string; className: string; messageClassName: string}
> = {
  pass: {glyph: '✓', className: 'text-dyna-ok', messageClassName: 'text-dyna-ink-mid'},
  warn: {glyph: '!', className: 'text-dyna-warn', messageClassName: 'text-dyna-ink-mid'},
  // The only one that reads at full strength: it is the row stopping the post.
  fail: {glyph: '✕', className: 'text-dyna-err', messageClassName: 'text-dyna-ink'},
};

export const READINESS_LABEL: Record<BatchResponseReadiness, string> = {
  ready: 'Ready',
  check: 'Check',
  blocked: 'Blocked',
  posted: 'Posted',
};

/**
 * What a claim is called on screen.
 *
 * Per LINE, not per entry: one voucher covers a person's advances and their
 * reimbursements together, so the kind describes where a debit came from
 * rather than which queue it belongs to.
 */
export const CLAIM_LABEL: Record<BatchLineResponseSourceType, string> = {
  advance_request: 'Advance',
  reimbursement: 'Reimbursement',
};

/** What each voucher books (DERP-288), as the queue row names it. */
export const ENTRY_LABEL: Record<BatchResponseEntryKind, string> = {
  reimbursement: 'Reimbursement',
  advance_payout: 'Advance payout',
  advance_verified: 'Advance verified',
  // Recovered from pay: payroll debited, the person credited.
  advance_unproven: 'Failure of proof',
  // Gathered before the split, reimbursements and closed advances together.
  combined: 'Combined',
};

/**
 * Who an entry is for. Empty when the requester has no User row -- a bare id
 * would name someone the reviewer cannot look up, so say so.
 */
export const requesterName = ({requester_name: name}: {requester_name: string}): string =>
  name || 'Unnamed requester';

/** The page's stage tabs. Each voucher names its own (`BatchResponse.tab`). */
export type JournalTab = BatchResponseTab;

/**
 * The kinds a tab's Type filter picks between. Payout holds reimbursements
 * paid and advances paid out, and Finance work through one kind at a time. A
 * tab left out holds one kind, so it has no filter.
 */
export const FILTER_KINDS: Partial<Record<JournalTab, ReadonlyArray<BatchResponseEntryKind>>> = {
  payout: ['reimbursement', 'advance_payout'],
};

/**
 * Said wherever a Failure of proof voucher can be posted: on its tab and
 * beside its Post button. The voucher books a recovery from pay, so posting it
 * before payroll has actually taken the money states a deduction that never
 * happened (the owner, 2026-10-13).
 */
export const UNPROVEN_WARNING =
  'These amounts are deducted from the staff member’s salary. Post only after confirming with payroll that the deduction has actually been made.';

/**
 * What the queue's header says about the whole period.
 *
 * A total across every batch, including the posted ones, because the question
 * it answers is "what does this period come to" -- and a figure that silently
 * dropped everything already sent would fall as Finance worked the queue.
 */
export const queueTotal = (batches: ReadonlyArray<BatchResponse>): number =>
  batches.reduce((sum, batch) => sum + Number(batch.total), 0);

type DocumentRow = Pick<BatchDocumentResponse, 'source_type' | 'source_id' | 'day'>;

/**
 * A documents row's key. A Failure of proof voucher wider than a day lists an
 * advance once per day a line of it failed, each row with its `day`.
 */
export const documentKey = (document: DocumentRow): string =>
  `${document.source_type}:${document.source_id}:${document.day ?? ''}`;

/** How many claims the documents list holds: an advance listed per day counts once. */
export const claimCount = (documents: ReadonlyArray<DocumentRow>): number =>
  new Set(documents.map((document) => `${document.source_type}:${document.source_id}`)).size;
